import React, { useState, useEffect, useRef } from 'react';
import './Eyeglasses.scss';

import { useDispatch, useSelector } from 'react-redux';
import useFrames from '../../utils/hooks/useFrames';
import useIntersectionObserver from '../../utils/hooks/useIntersectionObserver';

import orderSlice from '../../store/slices/order.slice';
import favoriteSlice from '../../store/slices/favorites.slice';
import { getFavorites } from '../../store/slices/favorites.slice';

import { sortBy, display } from '../../constants/filter.constants';
import Frame from '../../store/interfaces/frame.interface';
import FilterSorting from '../../types/frame-sort.type';

import { Button, Dropdown, Icon, RouterLink, LazyLoad } from '../../components/ui/index';

import Breadcrumbs from '../../components/breadcrumbs/Breadcrumbs';
import loadingSlice from '../../store/slices/loading.slice';

interface props {
  gender?: 'Men' | 'Women' | 'Unisex' | 'all';
}

const Eyeglasses: React.FC<props> = ({ gender = 'all' }) => {
  const frameGender = [
    { path: 'mens', label: 'Men' },
    { path: 'women', label: 'Women' },
    { path: 'unisex', label: 'Unisex' },
  ];

  const [frames, setFrames] = useState([] as Frame[]);
  const [filterFrames, setFilterFrames] = useState({
    pageNum: 0,
    pageCount: 12,
    sortBy: '',
    sortDir: 'asc',
    gender,
  });
  const [isFetching, setIsFetching] = useState(false);

  const framesLength = frames.length;
  const { getAllFrames } = useFrames();
  const dispatch = useDispatch();

  const loadingRef = React.createRef<HTMLDivElement>();
  const { isIntersecting: isLoading, setIntersectingFalse } = useIntersectionObserver(loadingRef);

  let paginationCnt = useRef<number>(0);
  let pageNum = useRef<number>(0);
  const { favorites } = useSelector(getFavorites);

  useEffect(() => {
    dispatch(loadingSlice.actions.setLoading(true));
  }, []);

  useEffect(() => {
    getAllFrames(
      {
        onSuccess: data => {
          paginationCnt.current = data.paginationCount;
          pageNum.current = data.pageNum;

          if (pageNum.current === paginationCnt.current) {
            setIntersectingFalse();
          }

          setFrames(data.frames);
          setIsFetching(false);
        },
        onError: err => {
          console.log(err);
        },
      },
      filterFrames,
    );
  }, [filterFrames]);

  useEffect(() => {
    if (isLoading && isFetching === false && paginationCnt.current >= pageNum.current + 1) {
      setIsFetching(true);

      setFilterFrames(filter => {
        return {
          ...filter,
          pageNum: pageNum.current + 1,
        };
      });
    }
  }, [isLoading]);

  const handleFrameClick = async (frame: Frame) => {
    dispatch(orderSlice.actions.setFrame(frame));
  };

  const handleFrameCountChanged = (e: Event) => {
    const el = e.target as HTMLSelectElement;

    setFilterFrames(filter => {
      return {
        ...filter,
        pageNum: 0,
        pageCount: parseInt(el.value),
      };
    });
  };

  const handleSorting = (e: Event) => {
    const el = e.target as HTMLSelectElement;
    const sorting = parseInt(el.value);

    let sortBy = '';
    let sortDir = '';

    switch (sorting) {
      case FilterSorting.MODEL_NAME_ASC: {
        sortBy = 'modelName';
        sortDir = 'asc';
        break;
      }
      case FilterSorting.MODEL_NAME_DESC: {
        sortBy = 'modelName';
        sortDir = 'desc';
        break;
      }
      case FilterSorting.PRICE_ASC: {
        sortBy = 'price';
        sortDir = 'asc';
        break;
      }
      case FilterSorting.PRICE_DESC: {
        sortBy = 'price';
        sortDir = 'desc';
        break;
      }
    }

    setFilterFrames(filter => {
      return {
        ...filter,
        sortBy,
        sortDir,
      };
    });
  };

  const handleFavoriteClick = async (frame: Frame) => {
    const exist = favorites.find(f => f === frame);
    if (!exist) {
      dispatch(favoriteSlice.actions.addFavorites(frame));
    }
  };

  const isFavorite = (frame: Frame) => {
    return favorites.find(f => f.id === frame.id) ? true : false;
  };

  useEffect(() => {}, [frames, favorites]);

  return (
    <>
      <Breadcrumbs
        path={`Eyeglasses${gender && gender !== 'all' ? '/' + gender : ''}`}
        background="eyeglasses"
      ></Breadcrumbs>
      <div className="eyeglasses-wrapper">
        <div className="first-sidebar">
          <div className="block block-category-navigation">
            <h2 className="text heading black uppercase">Eyeglasses</h2>
            <div className="break-line"></div>
            <div className="age-filter">
              <ul>
                {frameGender.map(g => (
                  <li key={`gender-${g.label}`}>
                    <RouterLink path={g.path} className={gender === g.label ? 'active' : ''}>
                      {g.label}
                    </RouterLink>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
        <div className="content">
          <div className="product-selectors">
            <div className="product-sorting">
              <div className="product-dropdown">
                <span>Sort By </span>
                <Dropdown optionsItems={sortBy} valueChanged={handleSorting}></Dropdown>
              </div>
            </div>
            <div className="product-page-size">
              <span>Display </span>
              <Dropdown optionsItems={display} valueChanged={handleFrameCountChanged}></Dropdown>
              <span>Per Page</span>
            </div>
          </div>
          <ul className="eyeglasses-list">
            {framesLength > 0 &&
              frames.map(i => (
                <li key={`frame-${i.id}`}>
                  <RouterLink
                    path={`/eyeglasses/${i.modelCode}`}
                    click={async () => await handleFrameClick(i)}
                  >
                    <LazyLoad imgSrc={i.previews[0]} imgAlt="Nice" />
                    {/* <img src={i.previews[0]} alt="Nice" /> */}
                  </RouterLink>
                  <div className=" eyeglasses-info-container">
                    <h3>{i.modelName}</h3>
                    <span className="price actual-price">${i.price.toFixed(2)}</span>
                  </div>
                  <div className="tools">
                    <Icon
                      className={`fav__button${isFavorite(i) ? ' active' : ''}`}
                      imgPath="/images/icons/favorite-icon.png"
                      click={async () => await handleFavoriteClick(i)}
                    ></Icon>
                    <RouterLink
                      path={`/eyeglasses/${i.modelCode}`}
                      click={async () => await handleFrameClick(i)}
                      customStyle={{ textDecoration: 'none' }}
                    >
                      <Button className="button secondary-button tools__button">
                        View Details
                      </Button>
                    </RouterLink>
                  </div>
                </li>
              ))}
          </ul>
          <div className={`loading ${isFetching ? 'intersected' : ''}`} ref={loadingRef}>
            <Icon imgPath="/images/spin.svg" />
          </div>
        </div>
      </div>
    </>
  );
};

export default Eyeglasses;
